/**
 * Closing Costs Calculator Utility
 * Centralized logic for calculating closing costs to avoid duplication
 */

/**
 * Calculate mansion tax based on asking price and thresholds
 */
export const calculateMansionTax = (price, thresholds) => {
  if (!thresholds || !price) return 0;

  let applicableRate = 0;
  for (const threshold of thresholds) {
    if (price >= threshold.min) {
      applicableRate = threshold.rate;
    } else {
      break;
    }
  }

  return Math.round((price * applicableRate) / 100);
};

/**
 * Calculate transfer taxes based on asking price and thresholds
 */
export const calculateTransferTaxes = (price, thresholds) => {
  if (!thresholds || !price) return 0;

  let applicableTotal = 0;
  for (const threshold of thresholds) {
    if (price >= threshold.min) {
      applicableTotal = threshold.total;
    } else {
      break;
    }
  }

  return Math.round((price * applicableTotal) / 100);
};

/**
 * Calculate mortgage recording tax
 */
export const calculateMortgageRecordingTax = (price, config) => {
  if (!config || !price) return 0;

  const rate = price < config.threshold ? config.rate_below : config.rate_above;
  return Math.round((price * rate) / 100);
};

/**
 * Calculate title insurance
 */
export const calculateTitleInsurance = (price, rate) => {
  if (!rate || !price) return 0;
  return Math.round((price * rate) / 100);
};

/**
 * Calculate mortgage title insurance
 */
export const calculateMortgageTitleInsurance = (price, rate) => {
  if (!rate || !price) return 0;
  return Math.round((price * rate) / 100);
};

/**
 * Calculate total closing costs based on configuration
 * @param {Object} params - Calculation parameters
 * @returns {Object} Breakdown of closing costs
 */
export const calculateClosingCosts = ({
  askingPrice,
  buildingType = "Condo",
  saleType = "Resale",
  financing = "Yes",
  config,
  editableFees = {}, // Optional: override default fees
  otherCosts = [], // Optional: additional costs array
  closingCostCredit = 0
}) => {
  if (!config) {
    throw new Error('Config is required for closing costs calculation');
  }

  const fees = config.fees || {};

  // Start with fees (use editable fees if provided, otherwise use config defaults)
  const costs = {
    applicationFee: editableFees.applicationFee ?? fees.application_fee ?? 0,
    appraisalFee: editableFees.appraisalFee ?? fees.appraisal_fee ?? 0,
    creditCheck: editableFees.creditCheck ?? fees.credit_check ?? 0,
    bankAttorney: editableFees.bankAttorney ?? fees.bank_attorney ?? 0,
    recordingFee: editableFees.recordingFee ?? fees.recording_fee ?? 0,
    attorney: editableFees.attorney ?? fees.attorney ?? 0,
    workingCapital: editableFees.workingCapital ?? fees.working_capital ?? 0,
    closerFee: editableFees.closerFee ?? fees.closer_fee ?? 0,
  };

  // Calculate mansion tax
  costs.mansionTax = calculateMansionTax(askingPrice, config.mansion_tax?.thresholds);

  // Calculate transfer taxes (only for Sponsor sales)
  costs.transferTaxes = saleType === "Sponsor"
    ? calculateTransferTaxes(askingPrice, config.transfer_tax?.thresholds)
    : 0;

  // Calculate mortgage recording tax (only for non-Coop/Condop with financing)
  costs.mortgageRecordingTax = (buildingType !== "Coop" && buildingType !== "Condop" && financing === "Yes")
    ? calculateMortgageRecordingTax(askingPrice, config.mortgage_recording_tax?.condo)
    : 0;

  // Calculate title insurance (only for non-Coop/Condop)
  costs.titleInsurance = (buildingType !== "Coop" && buildingType !== "Condop")
    ? calculateTitleInsurance(askingPrice, config.title_insurance?.condo_rate)
    : 0;

  // Calculate mortgage title insurance (only for non-Coop/Condop)
  costs.mortgageTitleInsurance = (buildingType !== "Coop" && buildingType !== "Condop")
    ? calculateMortgageTitleInsurance(askingPrice, config.mortgage_title_insurance?.rate)
    : 0;

  // Calculate totals
  const totalFees = Object.values(costs).reduce((sum, value) => sum + value, 0);
  const totalOtherCosts = otherCosts.reduce((sum, item) => sum + (item.amount || 0), 0);
  const totalBeforeCredit = totalFees + totalOtherCosts;
  const netClosingCosts = totalBeforeCredit - closingCostCredit;

  return {
    costs,
    totalFees,
    totalOtherCosts,
    totalBeforeCredit,
    closingCostCredit,
    netClosingCosts: Math.round(netClosingCosts)
  };
};

/**
 * Calculate default closing costs (for initial page load)
 * Assumes defaults: Resale, financing = Yes
 */
export const calculateDefaultClosingCosts = async (propertyId, askingPrice, buildingType, authFetch) => {
  try {
    const response = await authFetch(`/config/closing_costs?property_id=${propertyId}`);

    if (!response || !response.data) {
      console.warn('No closing costs config found');
      return 0;
    }

    const result = calculateClosingCosts({
      askingPrice,
      buildingType,
      saleType: "Resale", // Default to Resale
      financing: "Yes", // Default to Yes (most common)
      config: response.data,
      closingCostCredit: 0
    });

    return result.netClosingCosts;
  } catch (error) {
    console.error('Error calculating default closing costs:', error);
    return 0;
  }
};
